<template>
  <div class="py-6">
    <!-- pb-0! neutralises LabelValue's own pb-8 so the grid owns row spacing -->
    <div class="grid grid-cols-1 gap-x-16 gap-y-4 tablet:grid-cols-2">
      <LabelValue class="pb-0! tablet:col-span-2" :label="t('LeadDetail.field.name')" data-testid="lead-detail-name">
        {{ lead.senderName || '---' }}
      </LabelValue>
      <LabelValue class="pb-0! tablet:col-span-2" :label="t('LeadDetail.status.label')" data-testid="lead-detail-status">
        <ChipBadge :color="badgeColorByStatus[lead.status]" rounded data-testid="lead-status-chip">
          {{ t(leadStatusLabelKey(lead.status)) }}
        </ChipBadge>
      </LabelValue>
      <LabelValue class="pb-0!" :label="t('LeadDetail.field.company')" data-testid="lead-detail-company">
        {{ lead.senderCompany || '---' }}
      </LabelValue>
      <LabelValue class="pb-0!" :label="t('LeadDetail.field.position')" data-testid="lead-detail-position">
        {{ lead.senderCompanyPosition || '---' }}
      </LabelValue>
      <LabelValue class="pb-0!" :label="t('LeadDetail.field.email')" data-testid="lead-detail-email">
        <div class="flex items-center gap-1">
          <a
            :href="`mailto:${lead.senderEmail}`"
            data-testid="lead-detail-email-link"
            @click="trackLeadContact('email', 'lead_detail', 'mailto')"
            >{{ lead.senderEmail }}</a
          >
          <TooltipWrapper top>
            <template #trigger>
              <IconButton
                :icon="CopyIcon"
                size="extra-small"
                variant="ghost-neutral"
                :aria-label="t('Leads.action.copyEmail')"
                data-testid="lead-detail-copy-email"
                @click="onCopyEmail"
              />
            </template>
            <template #content>
              <div>{{ t('Leads.action.copyEmail') }}</div>
            </template>
          </TooltipWrapper>
        </div>
      </LabelValue>
      <LabelValue class="pb-0!" :label="t('LeadDetail.field.phone')" data-testid="lead-detail-phone">
        <div v-if="phone" class="flex items-center gap-1">
          <a :href="telHref(phone)" data-testid="lead-detail-phone-link" @click="trackLeadContact('phone', 'lead_detail', 'tel')">{{
            phone
          }}</a>
          <TooltipWrapper top>
            <template #trigger>
              <IconButton
                :icon="CopyIcon"
                size="extra-small"
                variant="ghost-neutral"
                :aria-label="t('Leads.action.copyPhone')"
                data-testid="lead-detail-copy-phone"
                @click="onCopyPhone"
              />
            </template>
            <template #content>
              <div>{{ t('Leads.action.copyPhone') }}</div>
            </template>
          </TooltipWrapper>
        </div>
        <template v-else>---</template>
      </LabelValue>
    </div>

    <LeadHistory
      class="-mx-6 mt-6 border-t border-subtle px-6 pt-6"
      :lead-id="lead.id"
      :entries="lead.history"
      :status="lead.status"
      :status-loading="loading"
      @update:status="onStatusChange"
    />
  </div>
</template>

<script setup lang="ts">
import { ChipBadge, IconButton, TooltipWrapper } from '@ramp106/omrjs-core-ui'
import { CopyIcon } from '@ramp106/omrjs-core-ui/icons'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useLeadStatus } from '@/composables/useLeadStatus'
import { type LeadStatusEnum } from '@/gql/reviews'
import LabelValue from '@/ui/LabelValue.vue'

import { trackLeadContact } from './leadContactTracking'
import LeadHistory from './LeadHistory.vue'
import { badgeColorByStatus, leadStatusLabelKey } from './leadStatus'
import { copyEmail, copyPhone, telHref } from './leadType'
import type { LeadDetailData } from './types'

const props = defineProps<{
  lead: LeadDetailData
}>()

const { t } = useI18n()
const { setStatus, loading } = useLeadStatus()

const phone = computed(() => props.lead.senderPhone ?? '')

function onStatusChange(status: LeadStatusEnum) {
  setStatus(props.lead.id, status)
}

function onCopyEmail() {
  copyEmail(props.lead.senderEmail)
  trackLeadContact('email', 'lead_detail', 'copy')
}

function onCopyPhone() {
  copyPhone(phone.value)
  trackLeadContact('phone', 'lead_detail', 'copy')
}
</script>
